/**
 * Environment variable accessor with allowlisted keys.
 *
 * Strategy:
 * 1. Check Vite build-time env (import.meta.env.VITE_<KEY>)
 * 2. Check runtime injection (window.__PPE_ENV__[KEY]) — for Docker runtime overrides
 * 3. Fallback to legacy REACT_APP_ prefix (import.meta.env.REACT_APP_<KEY>)
 *
 * SECURITY:
 * - Only allowlisted keys are accessible — no arbitrary passthrough
 * - VITE_* values are PUBLIC in the browser bundle — never place secrets here
 * - Backend secrets must NEVER appear in this file or any frontend code
 */

const ALLOWED_KEYS = [
  'API_URL',
  'SHOW_DEMO',
] as const;

type EnvKey = (typeof ALLOWED_KEYS)[number];

/** Runtime env injection shape (set by env-inject.sh in Docker) */
interface RuntimeEnv {
  [key: string]: string | undefined;
}

declare global {
  interface Window {
    __PPE_ENV__?: RuntimeEnv;
  }
}

function isAllowedKey(key: string): key is EnvKey {
  return (ALLOWED_KEYS as readonly string[]).includes(key);
}

/**
 * Read an environment variable by its short key name.
 *
 * @example
 * env('SHOW_DEMO') // checks VITE_SHOW_DEMO, then __PPE_ENV__.SHOW_DEMO, then REACT_APP_SHOW_DEMO
 * env('API_URL')    // checks VITE_API_URL, then __PPE_ENV__.API_URL, then REACT_APP_API_URL
 */
export function env(key: string): string | undefined {
  if (!isAllowedKey(key)) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[env] Key "${key}" is not in the allowlist. Add it to ALLOWED_KEYS in src/lib/env.ts`);
    }
    return undefined;
  }

  // 1. Vite build-time (primary)
  const viteKey = `VITE_${key}`;
  const viteVal = import.meta.env[viteKey];
  if (viteVal !== undefined && viteVal !== '') {
    return viteVal;
  }

  // 2. Runtime injection (Docker override)
  const runtimeVal = typeof window !== 'undefined' ? window.__PPE_ENV__?.[key] : undefined;
  if (runtimeVal !== undefined && runtimeVal !== '') {
    return runtimeVal;
  }

  // 3. Legacy REACT_APP_ fallback (transition support)
  const reactKey = `REACT_APP_${key}`;
  const reactVal = import.meta.env[reactKey];
  if (reactVal !== undefined && reactVal !== '') {
    return reactVal;
  }

  return undefined;
}

/** Convenience: is the demo banner enabled? */
export function isDemo(): boolean {
  return env('SHOW_DEMO') === 'true';
}
